import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'

import { httpOk, GET, POST, PUT } from 'helpers/api'
import { formatDate, formatMoney } from "helpers/helpers"

/**
 * Внешняя система лояльности бренда (UDS, Premium Bonus, свой сервис заведения).
 * Пока включена, баллы гостей ведёт она: касса показывает её баланс, списание баллов
 * резервируется в ней при закрытии чека, продажи уходят ей на начисление. Своя
 * лояльность Mison на бренде при этом не начисляет и не списывает.
 */
function ExternalProvider({ show, onHide, onChange }) {
	const { t } = useTranslation()

	const [form, setForm] = useState({ 'name': '', 'base_url': '', 'is_active': false })
	const [secret, setSecret] = useState('')
	const [stats, setStats] = useState({})
	const [operations, setOperations] = useState([])
	const [statusFilter, setStatusFilter] = useState('')
	const [check, setCheck] = useState({ 'phone': '' })
	const [checkResult, setCheckResult] = useState(null)

	function apply(data) {
		setForm({
			'name': data?.provider?.name ?? '',
			'base_url': data?.provider?.base_url ?? '',
			'is_active': !!data?.provider?.is_active,
		})
		setSecret(data?.provider?.secret ?? '')
		setStats(data?.stats ?? {})
	}

	async function getData() {
		const response = await GET('/loyalty-provider', {}, { loader: true })
		if (httpOk(response)) apply(response.data)
		getOperations()
	}

	async function getOperations(status = statusFilter) {
		const response = await GET('/loyalty-provider/operations', { 'size': 50, ...(status ? { status } : {}) })
		if (httpOk(response)) setOperations(response.data.data)
	}

	async function save(regenerateSecret = false) {
		const response = await PUT('/loyalty-provider', { ...form, 'regenerate_secret': regenerateSecret }, { loader: true })
		if (httpOk(response)) {
			apply(response.data)
			onChange?.(response.data)
		}
	}

	async function ping() {
		const response = await POST('/loyalty-provider/ping', {}, { loader: true, notification: false })
		if (!httpOk(response)) return
		if (response.data.ok) {
			toast.success(t('loyalty_provider_ping_ok'))
		} else {
			toast.error(t('loyalty_provider_ping_failed') + ': ' + (response.data.error || response.data.http_code || ''))
		}
	}

	async function checkCustomer() {
		setCheckResult(null)
		const response = await POST('/loyalty-provider/customer', check, { loader: true, notification: false })
		if (httpOk(response)) setCheckResult(response.data.customer)
		getOperations()
	}

	async function retry(item) {
		const response = await POST(`/loyalty-provider/operations/${item.id}/retry`, {}, { loader: true })
		if (httpOk(response)) getOperations()
	}

	function returnClassStatus(item) {
		if (item.status === 'done') return 'bg-success text-white'
		if (item.status === 'rejected' || item.status === 'failed') return 'bg-danger text-white'
		return 'bg-warning'
	}

	useEffect(() => {
		if (show) getData()
	}, [show]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Modal show={show} animation={false} centered scrollable size="xl" onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>{t('loyalty_provider')}</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ 'height': '640px' }}>
				<div className="alert alert-light border">
					{t('loyalty_provider_hint')}{' '}
					<a href="https://api.mison.uz/loyalty-provider" target="_blank" rel="noreferrer">{t('documentation')}</a>
				</div>

				<div className="d-flex gap-2">
					<div className="form-group w-100">
						<label>{t('loyalty_provider_name')}</label>
						<input type="text" className="form-control" placeholder="UDS"
							value={form.name}
							onChange={(e) => setForm({ ...form, 'name': e.target.value })} />
					</div>
					<div className="form-group w-100">
						<label>{t('loyalty_provider_url')}</label>
						<input type="text" className="form-control" placeholder="https://"
							value={form.base_url}
							onChange={(e) => setForm({ ...form, 'base_url': e.target.value })} />
					</div>
				</div>

				<div className="d-flex justify-content-between mb-2">
					<div className="fw-500 vertical-center">
						{t('loyalty_provider_active')}
						<div className="small text-muted">{t('loyalty_provider_active_hint')}</div>
					</div>
					<div className="form-check form-switch form-switch-lg">
						<input type="checkbox" className="form-check-input"
							checked={!!form.is_active}
							onChange={(e) => setForm({ ...form, 'is_active': e.target.checked })} />
					</div>
				</div>

				{!!secret &&
					<div className="form-group">
						<label>{t('webhook_secret')}</label>
						<div className="input-group">
							<input type="text" className="form-control" readOnly value={secret} />
							<button type="button" className="btn btn-outline-primary"
								onClick={() => navigator.clipboard.writeText(secret)}>
								<i className="uil-copy-alt"></i>
							</button>
							<button type="button" className="btn btn-outline-danger" onClick={() => save(true)}>
								{t('webhook_regenerate_secret')}
							</button>
						</div>
						<div className="small text-muted mt-1">{t('loyalty_provider_secret_hint')}</div>
					</div>
				}

				<div className="d-flex gap-2 mb-3">
					<button type="button" className="btn btn-primary" onClick={() => save(false)}>{t('save')}</button>
					<button type="button" className="btn btn-outline-primary" disabled={!secret} onClick={() => ping()}>
						{t('webhook_ping')}
					</button>
				</div>

				{!!secret &&
					<div className="d-flex gap-2 align-items-end mb-3">
						<div className="form-group mb-0" style={{ 'width': '260px' }}>
							<label>{t('loyalty_provider_check_guest')}</label>
							<input type="text" className="form-control" placeholder="998901234567"
								value={check.phone}
								onChange={(e) => setCheck({ 'phone': e.target.value.replace(/\D/g, '') })} />
						</div>
						<button type="button" className="btn btn-outline-primary" disabled={!check.phone}
							onClick={() => checkCustomer()}>
							{t('search')}
						</button>
						{!!checkResult &&
							<div className="ms-2">
								<b>{checkResult.name || '—'}</b> · {t('balance')}: <b>{formatMoney(checkResult.balance)}</b>
								{!!checkResult.level && <> · {checkResult.level}</>}
								{!checkResult.can_redeem && <span className="text-danger"> · {t('loyalty_provider_cannot_redeem')}</span>}
							</div>
						}
					</div>
				}

				<div className="d-flex justify-content-between align-items-center mb-2">
					<h6 className="mb-0">
						{t('loyalty_provider_operations')}
						{(Number(stats.failed) > 0 || Number(stats.rejected) > 0) &&
							<span className="text-danger ms-2">
								{t('loyalty_provider_problems')}: {Number(stats.failed) + Number(stats.rejected)}
							</span>
						}
					</h6>
					<select className="form-select" style={{ 'width': '200px' }}
						value={statusFilter}
						onChange={(e) => {
							setStatusFilter(e.target.value)
							getOperations(e.target.value)
						}}>
						<option value="">{t('all')}</option>
						<option value="pending">{t('webhook_status_pending')}</option>
						<option value="done">{t('loyalty_provider_status_done')}</option>
						<option value="rejected">{t('loyalty_provider_status_rejected')}</option>
						<option value="failed">{t('webhook_status_failed')}</option>
					</select>
				</div>
				<div className="table-responsive">
					<table className="table table-bordered mb-0 fz13">
						<thead>
							<tr>
								<th>{t('date')}</th>
								<th>{t('loyalty_provider_operation')}</th>
								<th className="text-center">{t('status')}</th>
								<th>{t('client')}</th>
								<th className="text-end">{t('loyalty_provider_points')}</th>
								<th className="text-end">{t('loyalty_provider_accrued')}</th>
								<th>{t('cheque')}</th>
								<th>{t('webhook_response')}</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{operations.map(item => (
								<tr key={item.id}>
									<td className="text-nowrap">{formatDate(item.created_at, 'dd.MM.yy HH:mm:ss')}</td>
									<td>{t('loyalty_provider_op_' + item.operation)}</td>
									<td className={`text-center ${returnClassStatus(item)}`}>{t('loyalty_provider_status_' + item.status)}</td>
									<td>{item.client_name || item.client_phone || item.external_customer_id || '—'}</td>
									<td className="text-end">{Number(item.amount) ? formatMoney(item.amount) : ''}</td>
									<td className="text-end">{item.accrued !== null ? formatMoney(item.accrued) : ''}</td>
									<td>{item.cheque_number ? '#' + item.cheque_number : ''}</td>
									<td className="text-break" style={{ 'maxWidth': '240px' }}>
										{item.http_code ? `HTTP ${item.http_code}` : ''}
										{!!item.error && <div className="small text-danger">{item.error}</div>}
									</td>
									<td className="text-center">
										{['commit', 'cancel', 'refund'].includes(item.operation) && item.status !== 'done' &&
											<button type="button" className="btn btn-sm btn-outline-primary" onClick={() => retry(item)}>
												{t('webhook_retry')}
											</button>
										}
									</td>
								</tr>
							))}
							{!operations.length &&
								<tr><td colSpan={9} className="text-center text-muted">{t('list_empty')}</td></tr>
							}
						</tbody>
					</table>
				</div>
			</Modal.Body>
		</Modal>
	)
}

export default ExternalProvider
